import React, { useState, useMemo } from "react";
import {
  BarChart3, Eye, Send, Star, Phone, MessageCircle, Instagram, MapPin, Globe, Facebook, Twitter, Youtube,
} from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const CLICK_EVENTS = ["phone", "whatsapp", "instagram", "maps", "website", "facebook", "twitter", "youtube"];

const EVENT_META = [
  { key: "phone", label: "Hemen Ara", icon: Phone, color: "#9E2A3B", bg: "#FBEEF0" },
  { key: "whatsapp", label: "WhatsApp", icon: MessageCircle, color: "#1EBE5A", bg: "#E7F7EE" },
  { key: "instagram", label: "Instagram", icon: Instagram, color: "#E1306C", bg: "#FBEAF1" },
  { key: "maps", label: "Konum / Yol Tarifi", icon: MapPin, color: "#3b5bdb", bg: "#EAEEFB" },
  { key: "website", label: "Website", icon: Globe, color: "#5b4d4f", bg: "#F3EDEE" },
  { key: "facebook", label: "Facebook", icon: Facebook, color: "#1877F2", bg: "#E8F0FE" },
  { key: "twitter", label: "Twitter", icon: Twitter, color: "#1DA1F2", bg: "#E8F5FE" },
  { key: "youtube", label: "YouTube", icon: Youtube, color: "#FF0000", bg: "#FDEAEA" },
];

const PERIODS = [
  { id: "day", label: "Bugün" },
  { id: "month", label: "Bu Ay" },
  { id: "year", label: "Bu Yıl" },
  { id: "all", label: "Tümü" },
  { id: "custom", label: "Tarih Aralığı" },
];

const iso = (d) => d.toISOString().slice(0, 10);

const StatsPanel = ({ stats }) => {
  const [period, setPeriod] = useState("month");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const history = useMemo(() => (stats?.history || []), [stats]);

  const range = useMemo(() => {
    const today = new Date();
    const y = today.getFullYear();
    const m = today.getMonth();
    if (period === "day") return { from: iso(today), to: iso(today) };
    if (period === "month") return { from: iso(new Date(y, m, 1)), to: iso(today) };
    if (period === "year") return { from: iso(new Date(y, 0, 1)), to: iso(today) };
    if (period === "custom") return { from: from || "0000-01-01", to: to || "9999-12-31" };
    return { from: "0000-01-01", to: "9999-12-31" };
  }, [period, from, to]);

  const filtered = useMemo(
    () => history.filter((h) => h.date >= range.from && h.date <= range.to),
    [history, range]
  );

  const totals = useMemo(() => {
    const t = { views: 0, quote: 0 };
    CLICK_EVENTS.forEach((e) => (t[e] = 0));
    filtered.forEach((h) => {
      t.views += h.views || 0;
      t.quote += h.quote || 0;
      CLICK_EVENTS.forEach((e) => (t[e] += h[e] || 0));
    });
    t.clicks = CLICK_EVENTS.reduce((a, e) => a + t[e], 0);
    return t;
  }, [filtered]);

  const chartData = useMemo(() => {
    const useMonthly = period === "year" || period === "all";
    if (useMonthly) {
      const buckets = {};
      filtered.forEach((h) => {
        const mk = h.date.slice(0, 7);
        if (!buckets[mk]) buckets[mk] = { label: mk.slice(2).replace("-", "/"), views: 0, clicks: 0 };
        buckets[mk].views += h.views || 0;
        buckets[mk].clicks += CLICK_EVENTS.reduce((a, e) => a + (h[e] || 0), 0);
      });
      return Object.keys(buckets).sort().map((k) => buckets[k]);
    }
    return filtered.map((h) => ({
      label: h.date.slice(5).replace("-", "/"),
      views: h.views || 0,
      clicks: CLICK_EVENTS.reduce((a, e) => a + (h[e] || 0), 0),
    }));
  }, [filtered, period]);

  return (
    <div className="bg-white rounded-2xl border border-[#F0DDE0] p-6" data-testid="stats-panel">
      <div className="flex items-center gap-2 mb-1">
        <BarChart3 className="w-5 h-5 text-[#9E2A3B]" />
        <h2 className="font-display text-xl text-[#2A2224]">İstatistikler</h2>
      </div>
      <p className="text-sm text-[#7C6E70] mb-4">İşletme sayfasındaki ziyaret ve tıklama sayıları.</p>

      <div className="flex flex-wrap items-center gap-2 mb-5">
        {PERIODS.map((p) => (
          <button
            key={p.id}
            type="button"
            data-testid={`stats-period-${p.id}`}
            onClick={() => setPeriod(p.id)}
            className={`px-3.5 py-1.5 rounded-full text-sm border transition-all ${period === p.id ? "bg-[#9E2A3B] text-white border-[#9E2A3B]" : "bg-white text-[#5b4d4f] border-[#F0DDE0] hover:border-[#E7A9B0]"}`}
          >
            {p.label}
          </button>
        ))}
        {period === "custom" && (
          <div className="flex items-center gap-2 ml-1">
            <input type="date" data-testid="stats-from" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 px-2 rounded-lg border border-[#F0DDE0] text-sm text-[#2A2224] focus:outline-none focus:ring-1 focus:ring-[#C15B6B]" />
            <span className="text-[#7C6E70] text-sm">–</span>
            <input type="date" data-testid="stats-to" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 px-2 rounded-lg border border-[#F0DDE0] text-sm text-[#2A2224] focus:outline-none focus:ring-1 focus:ring-[#C15B6B]" />
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <div data-testid="stat-value-views-card" className="rounded-2xl border border-[#E7A9B0] bg-gradient-to-br from-[#FBEEF0] to-white p-4 flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-white flex items-center justify-center shadow-sm"><Eye className="w-5 h-5 text-[#9E2A3B]" /></div>
          <div><p className="font-display text-2xl font-bold text-[#2A2224]" data-testid="stat-value-views">{totals.views}</p><p className="text-xs text-[#7C6E70]">Sayfa Görüntülenme</p></div>
        </div>
        <div className="rounded-2xl border border-[#E3CE9E] bg-gradient-to-br from-[#FBF3E4] to-white p-4 flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-white flex items-center justify-center shadow-sm"><Send className="w-5 h-5 text-[#C6A15B]" /></div>
          <div><p className="font-display text-2xl font-bold text-[#2A2224]" data-testid="stat-value-quote">{totals.quote}</p><p className="text-xs text-[#7C6E70]">Teklif İste Formu</p></div>
        </div>
        <div className="rounded-2xl border border-[#F0DDE0] bg-gradient-to-br from-[#FBF5F0] to-white p-4 flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-white flex items-center justify-center shadow-sm"><Star className="w-5 h-5 text-[#9E2A3B]" /></div>
          <div><p className="font-display text-2xl font-bold text-[#2A2224]" data-testid="stat-value-clicks">{totals.clicks}</p><p className="text-xs text-[#7C6E70]">Toplam Tıklama</p></div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {EVENT_META.map((it) => {
          const It = it.icon;
          return (
            <div key={it.key} data-testid={`stat-card-${it.key}`} className="rounded-2xl border border-[#F0DDE0] p-4 flex flex-col gap-2.5">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: it.bg }}>
                <It className="w-5 h-5" style={{ color: it.color }} />
              </div>
              <div><p className="font-display text-2xl font-bold text-[#2A2224]" data-testid={`stat-value-${it.key}`}>{totals[it.key]}</p><p className="text-xs text-[#7C6E70] mt-0.5">{it.label}</p></div>
            </div>
          );
        })}
      </div>

      <div className="mt-5">
        <h3 className="font-display text-base text-[#2A2224] mb-3">{period === "year" || period === "all" ? "Aylık Trend" : "Günlük Trend"}</h3>
        <div className="h-56 w-full">
          {chartData.length === 0 ? (
            <div className="h-full flex items-center justify-center text-sm text-[#7C6E70]">Seçilen aralıkta veri yok.</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="spViews" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#9E2A3B" stopOpacity={0.35} /><stop offset="100%" stopColor="#9E2A3B" stopOpacity={0} /></linearGradient>
                  <linearGradient id="spClicks" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#C6A15B" stopOpacity={0.35} /><stop offset="100%" stopColor="#C6A15B" stopOpacity={0} /></linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#F0DDE0" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#7C6E70" }} interval="preserveStartEnd" minTickGap={20} tickLine={false} axisLine={{ stroke: "#F0DDE0" }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#7C6E70" }} tickLine={false} axisLine={false} width={30} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #F0DDE0", fontSize: 13 }} formatter={(v, n) => [v, n === "views" ? "Görüntülenme" : "Tıklama"]} />
                <Area type="monotone" dataKey="views" stroke="#9E2A3B" strokeWidth={2} fill="url(#spViews)" name="views" />
                <Area type="monotone" dataKey="clicks" stroke="#C6A15B" strokeWidth={2} fill="url(#spClicks)" name="clicks" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="flex items-center gap-5 mt-3 text-sm">
          <span className="flex items-center gap-1.5 text-[#7C6E70]"><span className="w-3 h-3 rounded-full bg-[#9E2A3B]" /> Görüntülenme</span>
          <span className="flex items-center gap-1.5 text-[#7C6E70]"><span className="w-3 h-3 rounded-full bg-[#C6A15B]" /> Tıklama</span>
        </div>
      </div>
    </div>
  );
};

export default StatsPanel;
